import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash-es';
import { API, graphqlOperation } from 'aws-amplify';
import { getUserSelector } from '../../../../../../../../redux/reducers/reducerSlices/UserReducer';
import { sagaActions } from '../../../../../../../../sagas/sagaActions';
import { getPoSSselector } from '../../../../../../../../redux/reducers/reducerSlices/POSmartSchedulerReducer';
import { usePrevious } from '../../../../../../Common/CustomHooks/usePrevious';
import '../../../ReportsSection/SSAppointmentsReport/AllAppointmentsList/AllAppointmentsListSS.css';
import moment from 'moment-timezone';
import DrilldownTableSS from '../../../../Dashboard/DefaultDashboard/DrillDownReports/Common/DrilldownTableSS';
import { CustomPaginationSS } from '../../../../../../Common/CustomPaginationSS/CustomPaginationSS';
import {
  myReportsSelector,
  updateMyReportsList,
  updateSingleMyReport,
} from '../../../../../../../../redux/reducers/reducerSlices/MyReportsReducer';
import { smartScheduleSelector } from '../../../../../../../../redux/reducers/reducerSlices/SmartScheduleReducer';
import { getRemoteConfigSelector } from '../../../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import CustomDropDown from '../../../../../../Common/CustomDropDown/CustomDropDown';
import { formatDate_MM_DD_YYYY, shouldHavePermission } from '../../../../../../../Assests/Utility';
import DatePickerSS from '../../../../../../DatePickerSS/DatePickerSS';
import WarehouseFiltersForReports from '../../../ReportsSection/SSAppointmentsReport/AllAppointmentsList/Components/WarehouseFiltersForReports';
import {
  onCreateSPRODownloadReport,
  onUpdateSPRODownloadReport,
} from '../../../../../../../../graphql/subscriptions';
import { AppConstant } from '../../../../../../../Assests/AppConstant';

// Status filter options
const STATUS_OPTIONS = [
  { id: null, name: 'All Status' },
  { id: 'Pending', name: 'In Progress' },
  { id: 'Completed', name: 'Completed' },
];

const MyReports = (props) => {
  const { themeSelected = 'light-mode' } = props;
  const dispatch = useDispatch();

  // Selectors 
  const pOSS = useSelector(getPoSSselector);
  const { arrangeOrder, arrangeKey } = pOSS;

  const userObj = useSelector(getUserSelector);
  const { selectedWarehouse, userAccountDetails, permissions } = userObj;

  // MyReports has its own dedicated reducer slice
  const myReports = useSelector(myReportsSelector);
  const { myReportsList, myReportsLoading } = myReports;

  // Still need smartScheduleSelector for initateLoading (used for the table loader)
  const ss = useSelector(smartScheduleSelector);
  const { initateLoading } = ss;

  const remoteConfig = useSelector(getRemoteConfigSelector);
  const { allConfigration } = remoteConfig;
  const { PaginationDefaultLimit } = allConfigration;

  // Local state
  const divRef = useRef(null);
  const [currentTable, setCurrentTable] = useState(null);
  const [currentTableRef, setCurrentTableRef] = useState(null);
  const previousTable = usePrevious(currentTable);

  const [allowPermission, setAllowPermission] = useState(false);
  const [currentWarehouseFilterSelected, setCurrentWarehouseFilterSelected] = useState(null);

  // Status filter
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);

  // Report type filter
  const [reportTypeList, setReportTypeList] = useState([]);
  const [currentReportTypeFilterSelected, setCurrentReportTypeFilterSelected] = useState({ name: 'All Report type', id: null });

  // Date range
  const [dateRangeMode, setDateRangeMode] = useState('last30');
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [showStartDate, setShowStartDate] = useState(false);
  const [showEndDate, setShowEndDate] = useState(false);
  const [downloadWarehouseFilterSelected, setDownloadWarehouseFilterSelected] = useState(null);

  // Subscription-driven loader (onCreate = report is being generated)
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Subscription refs
  const createSubRef = useRef(null);
  const updateSubRef = useRef(null);

  // Ref to always hold the latest fetchList (avoids stale closure in subscriptions)
  const fetchListRef = useRef(null);

  // Table column definitions
  const apptsTitleDD = [
    { id: 'serial_num', name: 'S.No' },
    { id: 'ReportType', name: 'Report Type' },
    { id: 'StartDate', name: 'Start Date' },
    { id: 'EndDate', name: 'End Date' },
    { id: 'WarehouseName', name: 'Selected Warehouse' },
    { id: 'ReportEmail', name: 'To Email' },
    { id: 'Type', name: 'Type' },
    { id: 'Status', name: 'Status' },
    { id: 'CreatedOn', name: 'Created On' },
    { id: 'URL', name: 'Report' },
  ];

  // Permissions
  useEffect(() => {
    if (permissions.length) {
      const warehouseManagementPermission = _.find(permissions, { 'FeatureID': '12', 'PageAccess': true });
      if (warehouseManagementPermission && !_.isEmpty(warehouseManagementPermission)) {
        const temp = shouldHavePermission(permissions, '1000');
        setAllowPermission(temp);
        if (temp) {
          if (selectedWarehouse) {
            const warehouseName = selectedWarehouse.ExternalSiteID
              ? `${selectedWarehouse.ExternalSiteID} - ${selectedWarehouse.Name}`
              : selectedWarehouse.Name;
            setCurrentWarehouseFilterSelected({ ID: selectedWarehouse.ID, name: warehouseName })
          }
          else {
            setCurrentWarehouseFilterSelected({ ID: null, name: 'All Warehouse' })
          }
        }
      }
    }
  }, [permissions]);

  // Report type config
  useEffect(() => {
    if (allConfigration && allConfigration.ReportType) {
      setReportTypeList(JSON.parse(allConfigration.ReportType));
    }
  }, [allConfigration]);

  // Date range initialisation
  useEffect(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setSelectedStartDate(start);
    setSelectedEndDate(today);
  }, []);

  useEffect(() => {
    if (dateRangeMode === 'last30') {
      const today = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setSelectedStartDate(start);
      setSelectedEndDate(today);
    } else if (dateRangeMode === 'last60') {
      const today = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 60);
      setSelectedStartDate(start);
      setSelectedEndDate(today);
    } else if (dateRangeMode === 'custom') {
      const today = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      setSelectedStartDate(start);
      setSelectedEndDate(today);
    }
  }, [dateRangeMode]);

  const getWarehouseIDForFilter = useCallback(() => {
    if (currentWarehouseFilterSelected?.ID && allowPermission) {
      return currentWarehouseFilterSelected.ID;
    } else if (!currentWarehouseFilterSelected?.ID && allowPermission) {
      return null;
    } else {
      return selectedWarehouse?.ID || null;
    }
  }, [allowPermission, currentWarehouseFilterSelected, selectedWarehouse]);

  const getCompanyID = useCallback(() => {
    if (allowPermission) {
      return currentWarehouseFilterSelected?.ID ? null : selectedWarehouse?.CompanyID || null;
    }
    return null;
  }, [allowPermission, currentWarehouseFilterSelected, selectedWarehouse]);

  // Fetch list
  const fetchList = useCallback(
    (pageNo = 1, limit = PaginationDefaultLimit || 25) => {
      if (!userAccountDetails?.CUID) return;
      dispatch({
        type: sagaActions.FETCH_MY_REPORTS,
        payload: {
          CRUDTYPE: 'S',
          ID: null,
          CUID: userAccountDetails.CUID,
          IDWarehouse: getWarehouseIDForFilter(),
          CompanyID: getCompanyID(),
          ReportType: currentReportTypeFilterSelected?.name?.includes('All Report type') ? null : currentReportTypeFilterSelected?.name || null,
          StartDate: selectedStartDate
            ? moment(selectedStartDate).tz(moment.tz.guess()).format(AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss)
            : null,
          EndDate: selectedEndDate
            ? moment(selectedEndDate).tz(moment.tz.guess()).format(AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss)
            : null,
          Type: null,
          ReportEmail: null,
          Status: selectedStatus?.name?.includes('All Status') ? null : selectedStatus?.name || null,
          CreatedBy: null,
          URL: null,
          pageNo,
          limit: 50,
        },
      });
    },
    [
      userAccountDetails?.CUID,
      getWarehouseIDForFilter,
      getCompanyID,
      selectedStartDate,
      selectedEndDate,
      selectedStatus,
      currentReportTypeFilterSelected,
      PaginationDefaultLimit,
    ]
  );

  // Keep fetchListRef in sync with the latest fetchList on every render
  useEffect(() => {
    fetchListRef.current = fetchList;
  });

  // Fetch whenever filters change (and dates are initialised)
  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      fetchList(1);
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    selectedStatus,
    currentReportTypeFilterSelected,
    currentWarehouseFilterSelected,
  ]);

  // ── GraphQL Subscriptions ─────────────────────────────────────────────────
  const subscribeToReportEvents = useCallback(() => {
    if (!userAccountDetails?.CUID || !selectedWarehouse?.ID) {
      console.warn('[MyReports] subscribeToReportEvents skipped — missing CUID or IDWarehouse:', {
        CUID: userAccountDetails?.CUID,
        IDWarehouse: selectedWarehouse?.ID,
      });
      return;
    }

    const variables = {
      //  IDWarehouse: selectedWarehouse.ID,
      CUID: userAccountDetails.CUID,
    };

    console.log('[MyReports] Setting up subscriptions with variables:', variables);

    // ── onCreate → new report inserted → refetch list using latest filters ──
    if (createSubRef.current) {
      console.log('[MyReports] Unsubscribing previous onCreate subscription');
      createSubRef.current.unsubscribe();
    }

    const createSubObservable = API.graphql({
      ...graphqlOperation(onCreateSPRODownloadReport, variables),
      authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE,
    });
    console.log('[MyReports] onCreate observable created:', createSubObservable);

    createSubRef.current = createSubObservable.subscribe({
      next: ({ value }) => {
        console.log('[MyReports] onCreate subscription FIRED — connection is active. Payload:', value);
        const newReport = value?.data?.onCreateSPRODownloadReport;
        if (newReport) {
          setSubscriptionLoading(true);
          // Use fetchListRef to always call the latest fetchList with current
          fetchListRef.current?.();
          setSubscriptionLoading(false);
        } else {
          console.warn('[MyReports] onCreate fired but onCreateSPRODownloadReport is missing in payload:', value);
        }
      },
      error: (err) => {
        console.error('[MyReports] onCreate subscription ERROR (connection may have failed):', err);
      },
    });
    console.log('[MyReports] onCreate subscription established. isClosed:', createSubRef.current?.closed);

    // ── onUpdate → report finished → hide spinner + update specific record in-place (no API refetch) ──
    if (updateSubRef.current) {
      console.log('[MyReports] Unsubscribing previous onUpdate subscription');
      updateSubRef.current.unsubscribe();
    }

    const updateSubObservable = API.graphql({
      ...graphqlOperation(onUpdateSPRODownloadReport, variables),
      authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE,
    });
    console.log('[MyReports] onUpdate observable created:', updateSubObservable);

    updateSubRef.current = updateSubObservable.subscribe({
      next: ({ value }) => {
        console.log('[MyReports] onUpdate subscription FIRED — connection is active. Payload:', value);
        setSubscriptionLoading(false);
        const updatedReport = value?.data?.onUpdateSPRODownloadReport;
        if (updatedReport) {
          dispatch(updateSingleMyReport(updatedReport));
        } else {
          console.warn('[MyReports] onUpdate fired but onUpdateSPRODownloadReport is missing in payload:', value);
        }
      },
      error: (err) => {
        console.error('[MyReports] onUpdate subscription ERROR (connection may have failed):', err);
      },
    });
    console.log('[MyReports] onUpdate subscription established. isClosed:', updateSubRef.current?.closed);
  }, [
    userAccountDetails?.CUID,
    selectedWarehouse?.ID,
  ]);

  // Start subscriptions once user details are available; clean up on unmount
  useEffect(() => {
    if (userAccountDetails?.CUID && selectedWarehouse?.ID) {
      subscribeToReportEvents();
    } else {
      console.log('[MyReports] Subscription useEffect — waiting for CUID/IDWarehouse:', {
        CUID: userAccountDetails?.CUID,
        IDWarehouse: selectedWarehouse?.ID,
      });
    }
    return () => {
      console.log('[MyReports] Cleaning up subscriptions on unmount/re-run');
      if (createSubRef.current) createSubRef.current.unsubscribe();
      if (updateSubRef.current) updateSubRef.current.unsubscribe();
    };
  }, [userAccountDetails?.CUID, selectedWarehouse?.ID]);

  // Table ref tracking
  useEffect(() => {
    const container = divRef.current;
    if (!container) return;
    const tables = container.querySelectorAll('table');
    if (tables && tables.length && _.isEqual(currentTable, previousTable)) {
      setCurrentTableRef(container);
      setCurrentTable(tables);
    }
  });

  //  Format table body data status
  const getStatusCell = (status) => {
    if (status === 'IN PROGRESS') {
      return <p className='text-success mb-0'>In Progress</p>;
    } else if (status === 'COMPLETED') {
      return <p className='notScheStatusText mb-0'>Completed</p>;
    }
    else if (status === 'FAILED') {
      return <p className='notScheStatusText mb-0'>Failed</p>;
    }
    return <p className='notScheStatusText mb-0'>-</p>;
  };

  const formatMyReportsData = (data) =>
    data.map((el, index) => ({
      id: el.ID || index,
      0: (myReportsList.pageNo - 1) * myReportsList.limit + index + 1,
      1: el.ReportType || '-',
      2: el.StartDate ? moment(el.StartDate).utc().format('MM/DD/YYYY') : '-',
      3: el.EndDate ? moment(el.EndDate).utc().format('MM/DD/YYYY') : '-',
      4: el.WarehouseName || 'ALL',
      5: el.ReportEmail || '-',
      6: el.Type ? el.Type.toUpperCase() : '-',
      7: getStatusCell(el.Status),
      8: el.CreatedOn ? moment(el.CreatedOn).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '-',
      9: el.URL
        ? <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.open(el.URL, '_blank', 'noopener,noreferrer');
          }}
        >
          Download
        </a>
        : '-',
    }));

  const getBodyData = () => {
    const data = myReportsList?.data || [];
    return data.length ? formatMyReportsData(data) : [];
  };

  // Pagination
  const pageChange = (pageNo, limit = PaginationDefaultLimit || 25) => {
    const allData = myReportsList?.allData || [];
    if (allData.length) {
      const totalElements = allData.length;
      const totalPages = Math.ceil(totalElements / limit) || 1;
      const safePage = Math.min(Math.max(pageNo, 1), totalPages);
      const startIdx = (safePage - 1) * limit;
      const pageData = allData.slice(startIdx, startIdx + limit);
      dispatch(
        updateMyReportsList({
          data: pageData,
          allData,
          pageNo: safePage,
          limit,
          totalElements,
          last: safePage >= totalPages,
          first: safePage === 1,
        })
      );
    } else {
      fetchList(pageNo, limit);
    }
  };

  // loading flag
  const isLoading = myReportsLoading || initateLoading;

  return (
    <div className='appointments-report-ss-wrapper p-3'>
      <div className='container-fluid createPo-wrapper-ss my-2 mx-3'>
        <h4>My Reports</h4>

        {/* ── Header actions row ── */}
        <div className='d-flex justify-content-end align-items-center flex-wrap gap-2 all-appt-list-action-right'>

          {/* Date range selector */}
          <div className='d-flex appt-report-header align-items-center me-2'>
            <div className='me-2'>
              <CustomDropDown
                themeSelected={themeSelected}
                data={[
                  { id: 'last30', name: 'Last 30 Days' },
                  { id: 'last60', name: 'Last 60 Days' },
                  { id: 'custom', name: 'Choose Date' },
                ]}
                placeHolder={
                  dateRangeMode === 'last30'
                    ? 'Last 30 Days'
                    : dateRangeMode === 'last60'
                      ? 'Last 60 Days'
                      : 'Choose Date'
                }
                onSelect={(event) => setDateRangeMode(event.id)}
                keyStr='name'
                maxChars={15}
              />
            </div>

            {/* Custom date pickers */}
            {dateRangeMode === 'custom' && (
              <>
                <div className='pos-rel me-2'>
                  <div className='d-flex align-items-center'>
                    <span className='me-1'>Start Date:</span>
                    <button
                      className={`date-picker-btn h-100 mx-2 ${isLoading ? 'disabled' : ''}`}
                      onClick={() => setShowStartDate(!showStartDate)}
                      disabled={isLoading}
                    >
                      <span className='btn-title'>
                        {selectedStartDate
                          ? formatDate_MM_DD_YYYY(selectedStartDate)
                          : 'Start Date'}
                      </span>
                      <i className='fa-regular fa-calendar' style={{ marginInline: '9px' }} />
                    </button>
                    {showStartDate && (
                      <div className='appt-summary-ss-datePicker'>
                        <DatePickerSS
                          maxDate={selectedEndDate ? new Date(selectedEndDate) : new Date()}
                          setSelectedDate={(date) => {
                            setSelectedStartDate(date);
                            setSelectedEndDate(null);
                            setShowStartDate(false);
                          }}
                          selectedDate={selectedStartDate}
                          closeCalendar={() => setShowStartDate(!showStartDate)}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className='pos-rel me-2'>
                  <div className='d-flex align-items-center'>
                    <span className='me-1'>End Date:</span>
                    <button
                      className={`date-picker-btn h-100 mx-2 ${isLoading ? 'disabled' : ''}`}
                      onClick={() => setShowEndDate(!showEndDate)}
                      disabled={isLoading}
                    >
                      <span className='btn-title'>
                        {selectedEndDate
                          ? formatDate_MM_DD_YYYY(selectedEndDate)
                          : 'End Date'}
                      </span>
                      <i className='fa-regular fa-calendar' style={{ marginInline: '9px' }} />
                    </button>
                    {showEndDate && (
                      <div className='appt-summary-ss-datePicker'>
                        <DatePickerSS
                          minDate={selectedStartDate ? new Date(selectedStartDate) : undefined}
                          maxDate={
                            selectedStartDate
                              ? new Date(
                                Math.min(
                                  new Date(new Date().getFullYear(), 11, 31).getTime(),
                                  new Date(
                                    new Date(selectedStartDate).setMonth(
                                      new Date(selectedStartDate).getMonth() + 3
                                    )
                                  ).getTime()
                                )
                              )
                              : new Date(new Date().getFullYear(), 11, 31)
                          }
                          setSelectedDate={(date) => {
                            setSelectedEndDate(date);
                            setShowEndDate(false);
                          }}
                          selectedDate={selectedEndDate}
                          closeCalendar={() => setShowEndDate(!showEndDate)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Warehouse filter (super-admin only) */}
          {allowPermission && (
            <div className='mx-2'>
              <WarehouseFiltersForReports
                currentWarehouseFilterSelected={currentWarehouseFilterSelected}
                setCurrentWarehouseFilterSelected={setCurrentWarehouseFilterSelected}
                themeSelected={themeSelected}
              />
            </div>
          )}

          {/* Status filter */}
          <div className='me-2'>
            <CustomDropDown
              themeSelected={themeSelected}
              data={STATUS_OPTIONS}
              placeHolder={selectedStatus?.name || 'All Status'}
              onSelect={(event) => setSelectedStatus(event)}
              keyStr='name'
            />
          </div>

          {/* Report type filter */}
          {reportTypeList.length > 0 && (
            <div className='me-2'>
              <CustomDropDown
                themeSelected={themeSelected}
                data={[{ name: 'All Report type', id: null }, ...reportTypeList]}
                placeHolder={currentReportTypeFilterSelected?.name || 'All Report type'}
                onSelect={(event) => setCurrentReportTypeFilterSelected(event)}
                keyStr='name'
              />
            </div>
          )}

          {/* Subscription loading indicator */}
          {subscriptionLoading && (
            <div className='d-flex align-items-center me-2'>
              <span className='spinner-border spinner-border-sm text-primary me-1' role='status' />
              <small className='text-muted'>Generating report…</small>
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className='allApptsListContainer-ss' ref={divRef}>
        <DrilldownTableSS
          themeSelected={themeSelected}
          bodyData={getBodyData()}
          titleData={apptsTitleDD}
          showCollapse={false}
          showInput={false}
          showSort={false}
          hasCollapsableContent={false}
          arrangeOrder={arrangeOrder}
          arrangeKey={arrangeKey}
          drillDownReport={myReportsList}
          initiateDrillDownReport={isLoading}
          loaderArray={10}
          sortingAction={sagaActions.SORT_SS_USER_TABLE}
          subRoot={{ pathName: 'allApptsList' }}
          showUTCTime={false}
        />
      </div>

      {/* ── Pagination ── */}
      {!_.isEmpty(myReportsList) && myReportsList?.data?.length > 0 && (
        <div>
          <CustomPaginationSS
            pageChange={(pageNo, limit) => pageChange(pageNo, limit)}
            pageNo={myReportsList.pageNo}
            pageSize={myReportsList.limit}
            totalElements={myReportsList.totalElements}
            totalPages={Math.ceil(myReportsList.totalElements / myReportsList.limit)}
            last={myReportsList.last}
            first={myReportsList.first}
            data={myReportsList.data}
          />
        </div>
      )}
    </div>
  );
};

export default MyReports;
